// Components
import { Text, Field } from '@sitecore-jss/sitecore-jss-nextjs';
import { getEnum } from 'lib/get-enum';
import { Feature } from '.generated/templates/Feature.Items.model';
import { TemplatesIds } from 'lib/sitecore/template-ids';
import { Foundation } from '.generated/templates/Foundation.Items.model';
import { Sitecore } from '.generated/templates/_.Sitecore.Override';
import classNames from 'classnames';
import EditingHelpText, { MissingDataSource } from 'components/helpers/EditingHelpText';
import ImageWrapper, { SizedImageField } from 'components/helpers/ImageWrapper/ImageWrapper';
import LinkA11yWrapper from 'components/helpers/LinkA11yWrapper/LinkA11yWrapper';
import SvgIcon from 'components/helpers/Icons/SvgIcon/SvgIcon';
import Button from 'components/helpers/Button/Button';
import {
  ButtonIconNames,
  ButtonIconPosition,
} from 'components/helpers/Icons/ButtonLinkIcon/ButtonLinkIcon';
import { renderArticleFormattedDate } from 'lib/utils/datetime-utils';

export type NewsMediaReleasesProps = Sitecore.Override.ComponentBase &
  Feature.Components.Articles.Fields.NewsReleases & {
    fields: {
      newsReleases: (Sitecore.Override.ItemEx &
        Foundation.Pages.Fields.BaseArticle & {
          fields: {
            articleAuthor?: Field<string>;
            articleCategory?: Sitecore.Override.ItemEx & {
              fields?: {
                Value?: {
                  value: string;
                };
              };
            };
            internalModifiedDate?: Field<string>;
            lastUpdatedDate?: Field<string>;
            publishedDate?: Field<string>;
          };
        })[];
    };
  };

const NewReleases = ({ fields, rendering }: NewsMediaReleasesProps): JSX.Element => {
  // Fail out if we don't have any fields
  if (!fields) {
    return <MissingDataSource />;
  }
  const iconPrimary = getEnum<ButtonIconNames>(fields?.primaryCtaIcon);
  const iconPositionPrimary = getEnum<ButtonIconPosition>(fields?.primaryCtaIconLocation);
  const componentAnchorId = rendering?.uid;
  return (
    <div
      data-component="authorable/article/newsreleases"
      id={componentAnchorId}
      className="anchorIdIndentifier"
    >
      <div className="mb-6">
        {fields?.headline?.value && (
          <h2 className={classNames('my-6', 'text-base', 'tracking-wider', 'uppercase')}>
            {fields.headline?.value}
          </h2>
        )}

        {fields?.newsReleases?.length ? (
          <div className="border border-y-gray border-x-0 py-6 mb-6 w-full flex flex-col space-y-6 md:space-y-0 md:flex-row md:flex-wrap md:items-baseline justify-between">
            {fields?.newsReleases.map((newsRelease) => {
              const category =
                TemplatesIds.MediaArticle !== newsRelease.templateId
                  ? getEnum<string>(newsRelease.fields?.articleCategory)
                  : undefined;

              const publishedDateFormatted = renderArticleFormattedDate(
                newsRelease.fields?.publishedDate?.value,
                true
              );

              const lnkNewsDetailCTA = {
                value: {
                  href: newsRelease.url,
                  linktype: 'internal',
                },
              };
              return (
                <div
                  key={newsRelease.id}
                  className="w-full relative md:w-1/3 md:pr-4 md:last:pr-0 md:pb-0 flex flex-row justify-between items-start group"
                >
                  {/* MAKE TOP ALIGNED */}

                  <div className="w-28 h-28 mr-4 relative overflow-hidden rounded-lg">
                    <ImageWrapper
                      field={newsRelease.fields.imageFeatured as SizedImageField}
                      layout="fill"
                      imageClass={classNames('w-full', 'h-full', 'object-cover')}
                    />
                  </div>
                  <div className="flex-1">
                    {publishedDateFormatted.length ? (
                      <p className="text-gray-dark text-sm mb-2  group-hover:underline">
                        {publishedDateFormatted}
                      </p>
                    ) : (
                      <></>
                    )}
                    {newsRelease.fields.articleTitle ? (
                      <p className="mb-4 text-gray-darkest text-lg leading-4 group-hover:underline">
                        <Text field={newsRelease.fields.articleTitle} editable={false} />
                      </p>
                    ) : (
                      <></>
                    )}
                    {category ? (
                      <p className="text-blue-navy text-sm flex flex-row items-center gap-2">
                        <SvgIcon size={'em'} icon="large-bullet" viewBox="0 0 12 13" /> {category}
                      </p>
                    ) : (
                      <></>
                    )}
                  </div>
                  <LinkA11yWrapper
                    field={lnkNewsDetailCTA}
                    aria-label={newsRelease.fields.articleTitle.value}
                    className="stretched-link-hidden-text"
                  />
                </div>
              );
            })}
          </div>
        ) : (
          <EditingHelpText
            condition={!fields?.newsReleases?.length}
            text="New Releases | No feed selected or no articles in feed. "
          />
        )}

        {fields.primaryCta.value.text && (
          <div className="mt-0 mb-6">
            <Button
              field={fields.primaryCta}
              icon={iconPrimary}
              iconPosition={iconPositionPrimary}
              variant={getEnum(fields.primaryCtaStyle)}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default NewReleases;
